import type { MasterItem, StageKey } from "@/data/mockData";
import { TERMINAL_STAGE_KEYS, STAGE_KEY_ORDER } from "@/data/mockData";

/**
 * Resolve a stage label (stored on lead/deal) to its canonical StageKey.
 * Tries label match first; falls back to lowercase comparison for legacy data
 * where the label coincidentally equals the key (e.g. "Won" → "won").
 */
export function resolveStageKey(label: string, stages: MasterItem[]): StageKey | undefined {
  if (!label) return undefined;
  const byLabel = stages.find((s) => s.label === label);
  if (byLabel?.stageKey) return byLabel.stageKey;
  // Fallback: lowercase label may directly be a valid key (legacy/default labels)
  const lower = label.toLowerCase() as StageKey;
  if (STAGE_KEY_ORDER.includes(lower)) return lower;
  return undefined;
}

export function isTerminalStage(label: string, stages: MasterItem[]): boolean {
  const key = resolveStageKey(label, stages);
  return key !== undefined && TERMINAL_STAGE_KEYS.includes(key);
}

export function isWonStage(label: string, stages: MasterItem[]): boolean {
  return resolveStageKey(label, stages) === "won";
}

export function isLostStage(label: string, stages: MasterItem[]): boolean {
  return resolveStageKey(label, stages) === "lost";
}

export function isConvertedStage(label: string, stages: MasterItem[]): boolean {
  return resolveStageKey(label, stages) === "converted";
}

/** Won OR Converted — both represent a successful close. */
export function isClosedWonStage(label: string, stages: MasterItem[]): boolean {
  const key = resolveStageKey(label, stages);
  return key === "won" || key === "converted";
}

export function isActivePipelineStage(label: string, stages: MasterItem[]): boolean {
  return !isTerminalStage(label, stages);
}

export function getStageProbability(label: string, stages: MasterItem[]): number {
  const s = stages.find((item) => item.label === label);
  if (s?.defaultProbability !== undefined) return s.defaultProbability;
  // Fallback by key
  const key = resolveStageKey(label, stages);
  const byKey = stages.find((item) => item.stageKey === key);
  return byKey?.defaultProbability ?? 20;
}

/**
 * One rule for lead/deal probability:
 * - missing / null / undefined → stage default
 * - explicit 0 (or any number) stays as-is
 */
export function resolveProbability(
  value: number | null | undefined,
  stage: string,
  stages: MasterItem[],
): number {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return getStageProbability(stage, stages);
  }
  return Number(value);
}

/** Sort stage labels in canonical funnel order. Unknown labels go to the end. */
export function sortByStageOrder(labels: string[], stages: MasterItem[]): string[] {
  const sorted = [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const orderMap = new Map(sorted.map((s, i) => [s.label, i]));
  return [...labels].sort((a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999));
}
